import { getMe, isProblemError, type Me } from '@volee/api-client'
import { queryOptions, type QueryClient } from '@tanstack/react-query'

import { api } from '../services/api.ts'

// The current user is a server resource and not client state: `useQuery(['me'])`, never a
// store. Why, and what two copies cost: docs/design/frontend-v1.md §4.4.

export const ME_KEY = ['me'] as const

/** One definition for the guard and the components. `retry: false`: a 401 is an answer.
 *  `retryOnMount: false` keeps the shell from asking again as the door draws; the cost: after a
 *  network error a mount no longer retries it, the guards and the refetch on focus do. */
export const meQueryOptions = queryOptions({
  queryKey: ME_KEY,
  queryFn: (): Promise<Me | null> => getMe(api),
  // `null` is nobody, written only by the way out so the page left is never drawn empty (#255): a
  // component reads it as after a 401, and `loadMe`, which skips `select`, as no session.
  select: (me) => me ?? undefined,
  retry: false,
  retryOnMount: false,
  staleTime: 5 * 60 * 1000,
})

/** The current user, or `null` when there is no valid session. Anything that is not 401/403
 *  propagates: a backend that is down is not a permissions problem. */
export async function loadMe(queryClient: QueryClient): Promise<Me | null> {
  try {
    return await queryClient.ensureQueryData(meQueryOptions)
  } catch (error) {
    if (isProblemError(error) && (error.status === 401 || error.status === 403)) {
      return null
    }
    throw error
  }
}

/** Why the last `/v1/me` failed, if it did: `loadMe` flattens a 401/403 to `null`, and the reason
 *  stays in the query's state. Read from the cache, it asks nothing; `undefined` before any load. */
export function lastMeError(queryClient: QueryClient): unknown {
  return queryClient.getQueryState(ME_KEY)?.error
}

/** The session, or none — an unreachable backend included. Why the two public pages swallow
 *  what a guarded one propagates: apps/frontend/docs/razionale-che-il-codice-indica.md. */
export async function sessionOrNone(queryClient: QueryClient): Promise<Me | null> {
  try {
    return await loadMe(queryClient)
  } catch {
    return null
  }
}
